import { useForm } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import FdeSelect from '@/frontend/features/Fde/FdeSelect'
import ProjectPlanLinkPicker from '@/frontend/features/Fde/ProjectPlanLinkPicker'
import { fdeProjectPath } from '@/frontend/features/Fde/routes'
import type {
  PlanEditor,
  ProjectDelivery,
  ProjectPlan,
  ProjectPlanFiles,
} from '@/frontend/features/Fde/projectPlanTypes'
import type { FdeCopy, ProjectTask } from '@/frontend/features/Fde/types'

interface Props {
  editor: PlanEditor
  plan: ProjectPlan
  tasks: ProjectTask[]
  projectId: string
  copy: FdeCopy
  onClose: () => void
  fileDirectory?: ProjectPlanFiles
}

export default function ProjectPlanEditor({
  editor,
  plan,
  tasks,
  projectId,
  fileDirectory,
  copy,
  onClose,
}: Props) {
  const words = copy.project_plan
  const mode = editor.mode
  const validTaskIds = new Set(tasks.map((task) => task.id))
  const initialDelivery: ProjectDelivery = editor.delivery
    ? {
        ...editor.delivery,
        task_ids: editor.delivery.task_ids.filter((id) => validTaskIds.has(id)),
      }
    : {
        id: crypto.randomUUID(),
        title: '',
        acceptance_criteria: '',
        due_on: null,
        status: 'planned',
        task_ids: [],
        repo_paths: [],
      }
  const { accepted_at: _acceptedAt, accepted_by_label: _acceptedBy, ...delivery } = initialDelivery
  const form = useForm({
    scope: plan.scope ?? '',
    success_criteria: plan.success_criteria ?? '',
    lead_member_id: plan.lead_member_id ?? '',
    latest_update: plan.latest_update ?? '',
    next_step: plan.next_step ?? '',
    health: plan.health ?? '',
    health_reason: plan.health_reason ?? '',
    repo_paths: plan.repo_paths,
    delivery,
    lock_version: plan.lock_version,
  })
  const title =
    mode === 'direction'
      ? words.edit_direction
      : mode === 'checkpoint'
        ? words.update
        : editor.delivery
          ? words.edit_delivery
          : words.add_delivery
  function save(event: React.FormEvent) {
    event.preventDefault()
    form.transform((data) => {
      const fields =
        mode === 'direction'
          ? {
              scope: data.scope,
              success_criteria: data.success_criteria,
              lead_member_id: data.lead_member_id || null,
            }
          : mode === 'checkpoint'
            ? {
                latest_update: data.latest_update,
                next_step: data.next_step,
                health: data.health || null,
                health_reason: data.health_reason,
                repo_paths: data.repo_paths,
              }
            : { delivery: data.delivery }
      return { project_plan: { ...fields, lock_version: data.lock_version } }
    })
    form.patch(fdeProjectPath('/dashboard/discovery/project_plan', projectId), {
      preserveScroll: true,
      onSuccess: onClose,
    })
  }
  const textField = (
    key: 'scope' | 'success_criteria' | 'latest_update' | 'next_step' | 'health_reason',
    label: string,
    max: number,
    rows = 3,
  ) => (
    <div className="space-y-1.5">
      <Label htmlFor={`plan-${key}`}>{label}</Label>
      <Textarea
        id={`plan-${key}`}
        value={form.data[key]}
        maxLength={max}
        rows={rows}
        onChange={(event) => form.setData(key, event.target.value)}
      />
    </div>
  )
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !form.processing) onClose()
      }}
    >
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {mode === 'checkpoint'
              ? words.update_hint
              : mode === 'direction'
                ? words.setup_hint
                : words.acceptance_criteria}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          {mode === 'direction' && (
            <>
              {textField('scope', words.scope, 1000)}
              {textField('success_criteria', words.success_criteria, 600)}
              <div className="space-y-1.5">
                <Label>{words.lead}</Label>
                <FdeSelect
                  ariaLabel={words.lead}
                  value={form.data.lead_member_id}
                  onValueChange={(value) => form.setData('lead_member_id', value)}
                  options={[
                    { value: '', label: words.no_lead },
                    ...plan.members.map((member) => ({ value: member.id, label: member.name })),
                  ]}
                />
              </div>
            </>
          )}
          {mode === 'checkpoint' && (
            <>
              {textField('latest_update', words.last_update, 1000, 4)}
              {textField('next_step', words.next_step, 400, 2)}
              <FdeSelect
                ariaLabel={words.health}
                value={form.data.health}
                onValueChange={(value) => form.setData('health', value)}
                options={[
                  { value: '', label: words.health_unknown },
                  ...Object.entries(words.health_states).map(([value, label]) => ({
                    value,
                    label,
                  })),
                ]}
              />
              {form.data.health &&
                form.data.health !== 'on_track' &&
                textField('health_reason', words.health_reason, 240, 2)}
              <ProjectPlanLinkPicker
                projectId={projectId}
                fileDirectory={fileDirectory}
                plan={plan}
                copy={words}
                paths={form.data.repo_paths}
                onPaths={(value) => form.setData('repo_paths', value)}
              />
            </>
          )}
          {mode === 'delivery' && (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="delivery-title">{words.delivery_title}</Label>
                <Input
                  id="delivery-title"
                  required
                  maxLength={200}
                  value={form.data.delivery.title}
                  onChange={(event) =>
                    form.setData('delivery', { ...form.data.delivery, title: event.target.value })
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="delivery-criteria">{words.acceptance_criteria}</Label>
                <Textarea
                  id="delivery-criteria"
                  required
                  rows={3}
                  maxLength={600}
                  value={form.data.delivery.acceptance_criteria}
                  onChange={(event) =>
                    form.setData('delivery', {
                      ...form.data.delivery,
                      acceptance_criteria: event.target.value,
                    })
                  }
                />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="delivery-date">{copy.management.task_due_on}</Label>
                  <Input
                    id="delivery-date"
                    type="date"
                    value={form.data.delivery.due_on ?? ''}
                    onChange={(event) =>
                      form.setData('delivery', {
                        ...form.data.delivery,
                        due_on: event.target.value || null,
                      })
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>{copy.management.task_status}</Label>
                  <FdeSelect
                    ariaLabel={copy.management.task_status}
                    value={form.data.delivery.status}
                    onValueChange={(value) =>
                      form.setData('delivery', {
                        ...form.data.delivery,
                        status: value as ProjectDelivery['status'],
                      })
                    }
                    options={Object.entries(words.delivery_states)
                      .filter(([value]) => value !== 'accepted')
                      .map(([value, label]) => ({ value, label }))}
                  />
                </div>
              </div>
              <ProjectPlanLinkPicker
                projectId={projectId}
                fileDirectory={fileDirectory}
                plan={plan}
                copy={words}
                tasks={tasks}
                paths={form.data.delivery.repo_paths}
                taskIds={form.data.delivery.task_ids}
                onTasks={(value) =>
                  form.setData('delivery', { ...form.data.delivery, task_ids: value })
                }
                onPaths={(value) =>
                  form.setData('delivery', { ...form.data.delivery, repo_paths: value })
                }
              />
            </>
          )}
          {Object.values(form.errors).map((error, index) => (
            <p role="alert" className="text-sm text-destructive" key={index}>
              {error}
            </p>
          ))}
          <div className="flex justify-end gap-2 border-t border-border pt-4">
            <Button type="button" variant="outline" disabled={form.processing} onClick={onClose}>
              {copy.common.cancel}
            </Button>
            <Button type="submit" disabled={form.processing}>
              {form.processing ? copy.management.saving : copy.common.save}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
